/* ==============================================================================
   Seletor de microfone e de saída de som
   ==============================================================================
   Pedido: "deixa o mic e headset ao lado e opção de alterar o mic e a saída de
   som do sistema".

   Por que um painel e não dois seletores na barra
   ==============================================================================
   Dois `<select>` com nomes de dispositivo ("Microfone (Realtek(R) Audio)")
   ocupam mais largura que todos os controles de chamada somados. A barra tem uma
   quantidade fixa de espaço, e ela já é a peça mais disputada da tela.

   O painel abre a partir de uma seta ao lado do botão do microfone e do fone —
   o mesmo lugar onde qualquer aplicativo de chamada põe isso, então é onde a
   pessoa procura.

   Três coisas que esta tela precisa acertar, e que quase todo app erra
   ==============================================================================
   1. **`deviceId` não é estável entre sessões.** O navegador os regenera, e uma
      preferência salva por id volta como "dispositivo desconhecido" no dia
      seguinte. É por isso que o consumidor recebe também o `label`, para poder
      reencontrar por nome.

   2. **Sem permissão de microfone, `enumerateDevices` devolve entradas VAZIAS.**
      A lista existe, com o número certo de itens, e `label` é string vazia em
      todos. Mostrar isso produz um seletor com cinco linhas em branco. O
      estado certo é pedir a permissão, e é o que este componente faz.

   3. **A lista muda enquanto está aberta.** Plugar um fone é o momento MAIS
      provável de alguém abrir este painel. `ondevicechange` re-enumera.
   ============================================================================== */

import { useCallback, useEffect, useRef, useState } from 'react';

import { FloatingPanel, PopoverDivider, PopoverLabel } from '../components/Popover.tsx';

export interface AudioDevice {
  deviceId: string;
  label: string;
}

export interface DevicePickerProps {
  x: number;
  y: number;
  onClose: () => void;

  /** `deviceId` da entrada em uso, ou `null` para o padrão do sistema. */
  inputId: string | null;
  onSelectInput: (deviceId: string | null, label: string) => void;

  /** `deviceId` da saída em uso, ou `null`. */
  outputId: string | null;
  onSelectOutput: (deviceId: string | null, label: string) => void;

  /**
   * `false` esconde a seção de saída com explicação.
   *
   * `setSinkId` só existe no Chromium. No Firefox a escolha de saída passa por
   * `selectAudioOutput()`, que abre um diálogo do próprio navegador e não uma
   * lista nossa. Oferecer um seletor que não faz nada é pior que não oferecer.
   */
  canSelectOutput?: boolean | undefined;

  /**
   * Qual lista mostrar. `'both'` mostra as duas.
   *
   * A versão anterior mostrava SEMPRE as duas e destacava a pedida com uma barra
   * de acento. Estava errado por dois motivos, e o segundo é o que decide:
   *
   *   · as duas setas abriam um painel visualmente IDÊNTICO, então não havia
   *     como saber se o clique fez o que se esperava — "os menus de mic e fone
   *     mostram o mesmo menu";
   *
   *   · o painel com as duas seções é alto, e a âncora fica logo acima da barra
   *     inferior. Ele não cabia, e a seção de baixo — justamente a de saída —
   *     saía da tela.
   *
   * Uma lista por seta resolve os dois: o painel encolhe para caber, e o que ele
   * mostra é exatamente o que a seta prometia.
   */
  sections?: 'input' | 'output' | 'both' | undefined;
}

export function DevicePicker({
  x,
  y,
  onClose,
  inputId,
  onSelectInput,
  outputId,
  onSelectOutput,
  canSelectOutput = true,
  sections = 'both',
}: DevicePickerProps) {
  const [inputs, setInputs] = useState<AudioDevice[]>([]);
  const [outputs, setOutputs] = useState<AudioDevice[]>([]);
  const [needsPermission, setNeedsPermission] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enumerate = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const mic = devices.filter((d) => d.kind === 'audioinput');
      const spk = devices.filter((d) => d.kind === 'audiooutput');

      /*
       * Rótulo vazio em TODOS os itens significa falta de permissão.
       *
       * Não é "nenhum dispositivo": a lista vem com a contagem certa. O
       * navegador esconde os nomes até haver permissão, para a página não poder
       * fazer impressão digital do hardware sem consentimento. Checar `length`
       * aqui produziria "nenhum microfone encontrado" para quem tem cinco.
       */
      if (mic.length > 0 && mic.every((d) => d.label === '')) {
        setNeedsPermission(true);
        return;
      }

      setNeedsPermission(false);
      setInputs(
        mic.map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label !== '' ? d.label : `Microfone ${i + 1}`,
        })),
      );
      setOutputs(
        spk.map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label !== '' ? d.label : `Saída ${i + 1}`,
        })),
      );
    } catch {
      setError('Não foi possível listar os dispositivos de áudio.');
    }
  }, []);

  useEffect(() => {
    void enumerate();

    /*
     * Re-enumera quando o sistema muda.
     *
     * Plugar um fone é justamente o motivo mais comum de abrir este painel, e
     * sem isto o dispositivo novo não apareceria até fechar e abrir de novo.
     */
    const onChange = () => void enumerate();
    navigator.mediaDevices?.addEventListener?.('devicechange', onChange);
    return () => {
      navigator.mediaDevices?.removeEventListener?.('devicechange', onChange);
    };
  }, [enumerate]);

  const askPermission = useCallback(async () => {
    try {
      /*
       * Pede o stream só para destravar os rótulos, e o encerra na hora.
       *
       * Deixar a faixa viva acenderia a luz do microfone e o indicador de "em
       * uso" do sistema operacional por causa de uma tela de configuração — o
       * que legitimamente assusta. `getTracks().forEach(stop)` libera na hora.
       */
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      for (const track of stream.getTracks()) track.stop();
      await enumerate();
    } catch {
      setError(
        'Permissão de microfone negada. Libere o acesso nas configurações do ' +
          'navegador para escolher o dispositivo.',
      );
    }
  }, [enumerate]);

  return (
    <FloatingPanel
      x={x}
      y={y}
      /*
       * Painel de uma seção é mais estreito.
       *
       * 288px acomodava dois títulos e duas listas. Com uma lista, a largura
       * extra só afasta o texto do lugar onde o olho está — a seta que o abriu.
       */
      width={sections === 'both' ? 288 : 264}
      label={
        sections === 'input'
          ? 'Escolher microfone'
          : sections === 'output'
            ? 'Escolher saída de som'
            : 'Dispositivos de áudio'
      }
      role="dialog"
      onClose={onClose}
    >
      {error !== null ? (
        <p
          className="px-3 py-3"
          style={{
            fontSize: 'var(--text-2xs)',
            lineHeight: 'var(--leading-relaxed)',
            color: 'var(--color-danger-on-subtle)',
          }}
        >
          {error}
        </p>
      ) : needsPermission ? (
        <div className="px-3 py-3">
          <p
            style={{
              fontSize: 'var(--text-2xs)',
              lineHeight: 'var(--leading-relaxed)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            O navegador esconde o nome dos dispositivos até você permitir o
            microfone — é o que impede uma página de identificar seu hardware sem
            consentimento.
          </p>
          <button
            type="button"
            onClick={() => void askPermission()}
            className="mt-2.5 w-full px-3 py-2 font-semibold"
            style={{
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-accent-solid)',
              color: 'var(--color-on-solid)',
              fontSize: 'var(--text-2xs)',
            }}
          >
            Permitir para listar os dispositivos
          </button>
        </div>
      ) : (
        <>
          {(sections === 'input' || sections === 'both') && (
            <>
              <PopoverLabel>Microfone</PopoverLabel>
              <DeviceList
                devices={inputs}
                selectedId={inputId}
                onSelect={onSelectInput}
                emptyHint="Nenhum microfone encontrado."
                /*
                 * Foco no item selecionado DESTA lista.
                 *
                 * Com uma seção só isso é redundante com o foco que o
                 * `FloatingPanel` dá ao primeiro item do painel. Em `'both'` não
                 * é: lá o primeiro item é sempre da lista de microfones, e quem
                 * abriu pela seta do fone começaria navegando no lugar errado.
                 */
                autoFocus={sections === 'input'}
              />
            </>
          )}

          {sections === 'both' && <PopoverDivider />}

          {(sections === 'output' || sections === 'both') && (
            <>
              <PopoverLabel>Saída de som</PopoverLabel>
              {canSelectOutput ? (
                <DeviceList
                  devices={outputs}
                  selectedId={outputId}
                  onSelect={onSelectOutput}
                  emptyHint="Nenhuma saída encontrada."
                  autoFocus={sections === 'output'}
                />
              ) : (
                <p
                  className="px-3 pb-2.5"
                  style={{
                    fontSize: 'var(--text-2xs)',
                    lineHeight: 'var(--leading-relaxed)',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  Este navegador não permite escolher a saída pela página. O áudio
                  vai para o dispositivo padrão do sistema — troque-o nas
                  configurações de som do computador.
                </p>
              )}
            </>
          )}
        </>
      )}
    </FloatingPanel>
  );
}

/*
 * `SectionLabel` foi REMOVIDO com o destaque por barra de acento.
 *
 * Ele existia para marcar qual das duas seções a pessoa havia pedido. Com um
 * painel por seta não há o que desambiguar — o título do painel já diz — e um
 * componente de rótulo com duas variantes para um caso que não existe mais é
 * peso morto que alguém acabaria usando por engano.
 */

/* ==============================================================================
   Lista de um tipo de dispositivo
============================================================================== */

function DeviceList({
  devices,
  selectedId,
  onSelect,
  emptyHint,
  autoFocus = false,
}: {
  devices: AudioDevice[];
  selectedId: string | null;
  onSelect: (deviceId: string | null, label: string) => void;
  emptyHint: string;
  /**
   * Leva o foco do teclado para o item selecionado desta lista.
   *
   * O `FloatingPanel` foca o primeiro `[data-menu-item]` do painel ao abrir, e
   * esse item é sempre da PRIMEIRA seção. Sem isto, quem abre pela seta do fone
   * e navega por Tab começa na lista de microfones.
   */
  autoFocus?: boolean | undefined;
}) {
  if (devices.length === 0) {
    return (
      <p
        className="px-3 pb-2.5"
        style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
      >
        {emptyHint}
      </p>
    );
  }

  /*
   * "Padrão do sistema" é uma opção EXPLÍCITA, não a ausência de escolha.
   *
   * Sem ela, quem escolheu um fone específico e depois quer voltar a seguir o
   * sistema não tem como: nenhum item da lista significa "acompanhe o que o
   * sistema decidir". E acompanhar o sistema é o comportamento certo para a
   * maioria — é o que faz trocar de fone funcionar sem mexer no app.
   *
   * O navegador também expõe um dispositivo com `deviceId === 'default'`, mas
   * ele aparece DUPLICADO com o dispositivo real na maioria dos sistemas. Filtrar
   * e oferecer a opção própria evita a lista com o mesmo fone duas vezes.
   */
  const real = devices.filter((d) => d.deviceId !== 'default' && d.deviceId !== '');

  return (
    <div className="flex flex-col pb-1.5">
      <DeviceRow
        label="Padrão do sistema"
        selected={selectedId === null}
        autoFocus={autoFocus && selectedId === null}
        onClick={() => onSelect(null, 'Padrão do sistema')}
      />
      {real.map((device) => (
        <DeviceRow
          key={device.deviceId}
          label={device.label}
          selected={selectedId === device.deviceId}
          autoFocus={autoFocus && selectedId === device.deviceId}
          onClick={() => onSelect(device.deviceId, device.label)}
        />
      ))}
    </div>
  );
}

function DeviceRow({
  label,
  selected,
  autoFocus = false,
  onClick,
}: {
  label: string;
  selected: boolean;
  autoFocus?: boolean | undefined;
  onClick: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    /*
     * O foco é movido num efeito, e não pelo atributo `autoFocus` do JSX.
     *
     * O `FloatingPanel` foca o primeiro item do painel no seu próprio efeito de
     * montagem. Dois `autoFocus` declarativos concorreriam, e quem ganha depende
     * da ordem em que o React aplica os refs — que não é contrato. Um efeito
     * aqui roda DEPOIS do do pai (filhos montam primeiro, efeitos do pai rodam
     * depois... e é justamente por isso que o `preventScroll` importa: o pai
     * pode ter rolado o painel).
     */
    if (autoFocus) ref.current?.focus({ preventScroll: false });
  }, [autoFocus]);

  return (
    <button
      ref={ref}
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      data-menu-item
      onClick={onClick}
      className="flex w-full items-center gap-2 px-3 py-1.5 text-left"
      style={{
        fontSize: 'var(--text-2xs)',
        color: selected ? 'var(--color-accent-on-subtle)' : 'var(--color-text-secondary)',
        background: selected ? 'var(--color-accent-subtle)' : 'transparent',
      }}
      onMouseEnter={(event) => {
        if (!selected) event.currentTarget.style.background = 'oklch(100% 0 0 / 0.07)';
      }}
      onMouseLeave={(event) => {
        if (!selected) event.currentTarget.style.background = 'transparent';
      }}
    >
      {/*
        O marcador ocupa espaço SEMPRE, marcado ou não.
        =============================================
        Renderizado só quando selecionado, o texto de todas as outras linhas
        desloca 14px para a esquerda — e a lista inteira parece saltar quando se
        troca de dispositivo.
      */}
      <span
        aria-hidden="true"
        className="grid shrink-0 place-items-center"
        style={{ width: 14, height: 14 }}
      >
        {selected && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <path d="M4.5 12.5l5 5 10-11" />
          </svg>
        )}
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </button>
  );
}
